import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import PageHeader from "@/components/common/PageHeader";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { fmtINR } from "@/lib/format";
import { Plus, RefreshCw, Send, CheckCircle2, Trash2, CalendarDays, Clock, CalendarClock } from "lucide-react";
import { toast } from "sonner";

const METHODS = [
  { value: "months", label: "Monthly (equal per month)", icon: CalendarDays },
  { value: "days", label: "Per-day (weighted by days in each month)", icon: CalendarClock },
  { value: "hours", label: "Per-hour (needs total hours)", icon: Clock },
];

export default function PrepaidWizard() {
  const [items, setItems] = useState([]);
  const [drafts, setDrafts] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [depts, setDepts] = useState([]);
  const [ccs, setCcs] = useState([]);
  const [openNew, setOpenNew] = useState(false);
  const [openSchedule, setOpenSchedule] = useState(null);
  const [scheduleData, setScheduleData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({
    name: "", expense_account_id: "", deferred_account_id: "", cost_centre_id: "", department_id: "",
    total_amount: 0, start_date: new Date().toISOString().slice(0, 10),
    end_date: "", allocation_method: "months", total_hours: 0, narration: "",
  });

  const load = async () => {
    try {
      const [i, d] = await Promise.all([
        api.get("/prepaid"),
        api.get("/prepaid/drafts/pending"),
      ]);
      setItems(i.data); setDrafts(d.data);
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to load"); }
  };

  useEffect(() => {
    load();
    api.get("/accounts").then((r) => setAccounts(r.data || [])).catch(() => {});
    api.get("/departments").then((r) => setDepts(r.data || [])).catch(() => setDepts([]));
    api.get("/dimensions").then((r) => setCcs(r.data || [])).catch(() => setCcs([]));
  }, []);

  const create = async () => {
    if (!form.name || !form.expense_account_id || !form.deferred_account_id) return toast.error("Name + accounts required");
    if (!form.end_date) return toast.error("End date required");
    if (form.total_amount <= 0) return toast.error("Amount must be > 0");
    if (form.allocation_method === "hours" && form.total_hours <= 0) return toast.error("Total hours required for per-hour allocation");
    setBusy(true);
    try {
      const payload = { ...form,
                          cost_centre_id: form.cost_centre_id || null,
                          department_id: form.department_id || null,
                          total_hours: form.allocation_method === "hours" ? Number(form.total_hours) : null };
      await api.post("/prepaid", payload);
      toast.success("Prepaid schedule created — hit Refresh to generate drafts");
      setOpenNew(false);
      setForm({ ...form, name: "", total_amount: 0, end_date: "" });
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    setBusy(false);
  };

  const refresh = async () => {
    setBusy(true);
    try {
      const r = await api.post("/prepaid/refresh-drafts");
      toast.success(`${r.data.created} new drafts · ${r.data.updated} updated`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Refresh failed"); }
    setBusy(false);
  };

  const openSched = async (pp) => {
    setOpenSchedule(pp); setScheduleData(null);
    try {
      const r = await api.get(`/prepaid/${pp.id}/schedule`);
      setScheduleData(r.data);
    } catch (e) { toast.error("Failed to load schedule"); }
  };

  const post = async (draftId) => {
    try { await api.post(`/prepaid/drafts/${draftId}/post`); toast.success("Amortization posted"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const bulkPost = async () => {
    if (!window.confirm(`Post all ${drafts.length} pending amortization JVs?`)) return;
    setBusy(true);
    try {
      const r = await api.post("/prepaid/drafts/bulk-post");
      toast.success(`Posted ${r.data.posted} · Failed ${r.data.failed}`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Bulk post failed"); }
    setBusy(false);
  };

  const remove = async (pp) => {
    if (!window.confirm(`Delete prepaid schedule "${pp.name}"? Pending drafts will be removed too.`)) return;
    try { await api.delete(`/prepaid/${pp.id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  return (
    <div className="p-6 lg:p-8 space-y-6" data-testid="prepaid-page">
      <PageHeader eyebrow="Deferred expenses" title="Prepaid Expense Wizard"
                  description="Configure a prepaid schedule (rent · insurance · AMC · licence) once. Zenith drafts a monthly amortization JV for you to approve — refresh anytime to fold in new prepaid vouchers."
                  actions={
                    <div className="flex gap-2">
                      <Button variant="outline" onClick={refresh} disabled={busy} className="rounded-sm gap-1.5" data-testid="prepaid-refresh">
                        <RefreshCw className={"w-3.5 h-3.5 " + (busy ? "animate-spin" : "")} /> Refresh calculation
                      </Button>
                      <Button onClick={() => setOpenNew(true)} className="rounded-sm bg-accent hover:bg-accent/90 text-white gap-1.5" data-testid="prepaid-new">
                        <Plus className="w-3.5 h-3.5" /> New prepaid
                      </Button>
                    </div>
                  } />

      {/* KPI strip */}
      <div className="grid md:grid-cols-4 gap-3">
        <Card className="rounded-sm p-3" data-testid="prepaid-kpi-active">
          <div className="text-[10px] font-mono uppercase text-muted-foreground">Active schedules</div>
          <div className="text-2xl font-bold tabular-nums mt-1">{items.filter((x) => x.status === "active").length}</div>
        </Card>
        <Card className="rounded-sm p-3">
          <div className="text-[10px] font-mono uppercase text-muted-foreground">Pending drafts</div>
          <div className="text-2xl font-bold tabular-nums mt-1">{drafts.length}</div>
        </Card>
        <Card className="rounded-sm p-3">
          <div className="text-[10px] font-mono uppercase text-muted-foreground">Pending amount</div>
          <div className="text-2xl font-bold tabular-nums mt-1">{fmtINR(drafts.reduce((s, d) => s + Number(d.amount), 0))}</div>
        </Card>
        <Card className="rounded-sm p-3">
          <div className="text-[10px] font-mono uppercase text-muted-foreground">Total open value</div>
          <div className="text-2xl font-bold tabular-nums mt-1">{fmtINR(items.filter((x) => x.status === "active").reduce((s, i) => s + Number(i.total_amount) - (i.total_amount - Number(i.schedule_total || i.total_amount)), 0))}</div>
        </Card>
      </div>

      {/* Pending amortization drafts */}
      <Card className="rounded-sm" data-testid="prepaid-drafts-card">
        <div className="border-b px-4 py-2 flex items-center justify-between bg-amber-500/5">
          <div className="flex items-center gap-2">
            <span className="font-heading font-semibold text-amber-900">Pending amortization drafts</span>
            <span className="text-[10px] font-mono uppercase text-amber-700">// awaiting approval · {drafts.length}</span>
          </div>
          {drafts.length > 0 && (
            <Button size="sm" onClick={bulkPost} disabled={busy} className="rounded-sm gap-1 bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="prepaid-bulk-post">
              <CheckCircle2 className="w-3.5 h-3.5" /> Post all
            </Button>
          )}
        </div>
        <div className="overflow-auto">
          <table className="w-full dense text-sm">
            <thead><tr><th>Month</th><th>Prepaid</th><th className="text-right">Amount</th><th></th></tr></thead>
            <tbody>
              {drafts.length === 0 && <tr><td colSpan={4} className="text-center py-6 text-muted-foreground">No drafts — hit <span className="font-mono">Refresh calculation</span> to generate.</td></tr>}
              {drafts.map((d) => (
                <tr key={d.id} data-testid={`draft-${d.id}`}>
                  <td className="font-mono text-xs">{d.month}</td>
                  <td>{d.prepaid_name}</td>
                  <td className="text-right tabular-nums">{fmtINR(d.amount)}</td>
                  <td className="text-right">
                    <Button size="sm" variant="outline" onClick={() => post(d.id)} className="rounded-sm h-7 gap-1 text-xs" data-testid={`draft-post-${d.id}`}>
                      <Send className="w-3 h-3" /> Post
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      {/* Active prepaid schedules */}
      <Card className="rounded-sm overflow-hidden" data-testid="prepaid-list">
        <div className="border-b px-4 py-2 bg-accent/5">
          <span className="font-heading font-semibold">Prepaid schedules</span>
          <span className="text-[10px] font-mono uppercase text-muted-foreground ml-2">// {items.length} · click any row for month-by-month schedule</span>
        </div>
        <table className="w-full dense text-sm">
          <thead><tr><th>Name</th><th>Method</th><th>Period</th><th className="text-right">Total ₹</th><th className="text-right">Per month</th><th>Next</th><th>Status</th><th></th></tr></thead>
          <tbody>
            {items.length === 0 && <tr><td colSpan={8} className="text-center py-6 text-muted-foreground">No prepaid schedules yet.</td></tr>}
            {items.map((pp) => (
              <tr key={pp.id} onClick={() => openSched(pp)} className="cursor-pointer hover:bg-accent/5" data-testid={`prepaid-${pp.name.replace(/\s+/g, "-").toLowerCase()}`}>
                <td className="font-medium">{pp.name}</td>
                <td><span className="text-[10px] font-mono uppercase">{pp.allocation_method}</span></td>
                <td className="text-xs">{pp.start_date} → {pp.end_date}</td>
                <td className="text-right tabular-nums">{fmtINR(pp.total_amount)}</td>
                <td className="text-right tabular-nums text-muted-foreground">{fmtINR(pp.per_month_avg)}</td>
                <td className="text-xs">{pp.next_month ? `${pp.next_month} · ${fmtINR(pp.next_month_amount)}` : "—"}</td>
                <td>{pp.status === "completed" ? <span className="text-emerald-700 text-xs font-mono">✓ done</span> : <span className="text-blue-700 text-xs font-mono">active</span>}</td>
                <td className="text-right" onClick={(e) => e.stopPropagation()}>
                  {(!pp.posted_months || pp.posted_months.length === 0) && (
                    <button onClick={() => remove(pp)} className="text-muted-foreground hover:text-destructive" data-testid={`prepaid-del-${pp.name.replace(/\s+/g, "-").toLowerCase()}`}>
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>

      {/* New Prepaid Dialog */}
      <Dialog open={openNew} onOpenChange={setOpenNew}>
        <DialogContent className="rounded-sm sm:max-w-lg" data-testid="prepaid-new-dialog">
          <DialogHeader><DialogTitle>New prepaid expense schedule</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-[10px] uppercase font-mono">Description *</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Rent Apr 2026 → Mar 2027" className="rounded-sm mt-1" data-testid="pp-name" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-[10px] uppercase font-mono">Expense account *</Label>
                <Select value={form.expense_account_id} onValueChange={(v) => setForm({ ...form, expense_account_id: v })}>
                  <SelectTrigger className="rounded-sm mt-1" data-testid="pp-exp"><SelectValue placeholder="Select" /></SelectTrigger>
                  <SelectContent>
                    {accounts.filter((a) => a.account_type === "expense").map((a) => <SelectItem key={a.id} value={a.id}>{a.code} · {a.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-[10px] uppercase font-mono">Prepaid asset account *</Label>
                <Select value={form.deferred_account_id} onValueChange={(v) => setForm({ ...form, deferred_account_id: v })}>
                  <SelectTrigger className="rounded-sm mt-1" data-testid="pp-def"><SelectValue placeholder="Select" /></SelectTrigger>
                  <SelectContent>
                    {accounts.filter((a) => a.account_type === "asset").map((a) => <SelectItem key={a.id} value={a.id}>{a.code} · {a.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label className="text-[10px] uppercase font-mono">Amount ₹ *</Label>
              <Input type="number" value={form.total_amount} onChange={(e) => setForm({ ...form, total_amount: Number(e.target.value) })} className="rounded-sm mt-1 tabular-nums" data-testid="pp-amt" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-[10px] uppercase font-mono">Start date *</Label><Input type="date" value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} className="rounded-sm mt-1" data-testid="pp-start" /></div>
              <div><Label className="text-[10px] uppercase font-mono">End date *</Label><Input type="date" value={form.end_date} onChange={(e) => setForm({ ...form, end_date: e.target.value })} className="rounded-sm mt-1" data-testid="pp-end" /></div>
            </div>
            <div>
              <Label className="text-[10px] uppercase font-mono">Allocation methodology *</Label>
              <Select value={form.allocation_method} onValueChange={(v) => setForm({ ...form, allocation_method: v })}>
                <SelectTrigger className="rounded-sm mt-1" data-testid="pp-method"><SelectValue /></SelectTrigger>
                <SelectContent>{METHODS.map((m) => <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            {form.allocation_method === "hours" && (
              <div>
                <Label className="text-[10px] uppercase font-mono">Total hours *</Label>
                <Input type="number" value={form.total_hours} onChange={(e) => setForm({ ...form, total_hours: Number(e.target.value) })} className="rounded-sm mt-1 tabular-nums" data-testid="pp-hours" />
              </div>
            )}
            {depts.length > 0 && (
              <div>
                <Label className="text-[10px] uppercase font-mono">Department</Label>
                <Select value={form.department_id || "__none__"} onValueChange={(v) => setForm({ ...form, department_id: v === "__none__" ? "" : v })}>
                  <SelectTrigger className="rounded-sm mt-1"><SelectValue placeholder="—" /></SelectTrigger>
                  <SelectContent><SelectItem value="__none__">—</SelectItem>{depts.map((d) => <SelectItem key={d.id} value={d.id}>{d.code} · {d.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            )}
            <div>
              <Label className="text-[10px] uppercase font-mono">Narration</Label>
              <Input value={form.narration} onChange={(e) => setForm({ ...form, narration: e.target.value })} className="rounded-sm mt-1" data-testid="pp-narration" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpenNew(false)} className="rounded-sm">Cancel</Button>
            <Button onClick={create} disabled={busy} className="rounded-sm bg-accent hover:bg-accent/90 text-white" data-testid="pp-save">
              Create schedule
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Schedule Sheet */}
      <Sheet open={!!openSchedule} onOpenChange={(o) => { if (!o) { setOpenSchedule(null); setScheduleData(null); } }}>
        <SheetContent className="w-[560px] sm:max-w-[560px]" data-testid="prepaid-schedule-sheet">
          <SheetHeader><SheetTitle>{openSchedule?.name}</SheetTitle></SheetHeader>
          {scheduleData ? (
            <div className="mt-4 space-y-3">
              <div className="text-xs text-muted-foreground">
                {scheduleData.prepaid.start_date} → {scheduleData.prepaid.end_date} · {scheduleData.prepaid.allocation_method} · Total {fmtINR(scheduleData.prepaid.total_amount)}
              </div>
              <table className="w-full dense text-xs">
                <thead><tr><th>Month</th><th className="text-right">Amount</th><th>Status</th></tr></thead>
                <tbody>
                  {scheduleData.schedule.map((row) => (
                    <tr key={row.month}>
                      <td className="font-mono">{row.month}</td>
                      <td className="text-right tabular-nums">{fmtINR(row.amount)}</td>
                      <td>
                        {row.posted ? <span className="text-emerald-700 font-mono text-[10px]">✓ POSTED</span>
                          : row.draft_id ? <span className="text-amber-700 font-mono text-[10px]">DRAFT</span>
                          : <span className="text-muted-foreground text-[10px]">—</span>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : <div className="mt-4 text-sm text-muted-foreground">Loading schedule…</div>}
        </SheetContent>
      </Sheet>
    </div>
  );
}
